/**
 * Assignees for the records currently on screen.
 *
 * A list row does not carry its assignees — assignment lives in its own table,
 * and joining it onto every list query would push a many-to-many into a shape
 * that has to be flattened again. Instead the whole visible page is fetched in
 * one request, keyed by record id.
 *
 * Refetches when the visible set changes, which covers "load more" as well as
 * filtering. No cache: the request is one round trip against a small table, and
 * a stale assignee column is worse than an extra query.
 */

import { useEffect, useState } from 'react'
import type { SubjectType } from '@erp/contracts'
import { api } from './api'

export function useAssignees(
  type: SubjectType,
  ids: readonly string[],
): Map<string, string[]> {
  const [byRecord, setByRecord] = useState<Map<string, string[]>>(new Map())

  // Join the ids so the effect fires on membership rather than array identity,
  // which changes on every render.
  const key = ids.join(',')

  useEffect(() => {
    if (ids.length === 0) {
      setByRecord(new Map())
      return
    }

    let live = true
    void api.assignments.forSubjects(type, ids).then((assignments) => {
      if (!live) return
      const next = new Map<string, string[]>()
      for (const assignment of assignments) {
        const existing = next.get(assignment.subject.id)
        if (existing) existing.push(assignment.userId)
        else next.set(assignment.subject.id, [assignment.userId])
      }
      setByRecord(next)
    })

    // A slower earlier response must not overwrite a newer one.
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, key])

  return byRecord
}
